<template>
  <div>
    <div
      v-for="(sub, sIdx) in subQuestions"
      :key="sub.id || sIdx"
      class="border rounded p-3 mb-3"
    >
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="fw-semibold">Sub-question {{ sIdx + 1 }}</div>
        <button
          v-if="subQuestions.length > 1"
          type="button"
          class="btn btn-sm btn-outline-danger"
          @click="removeSubQuestion(sIdx)"
        >
          &times;
        </button>
      </div>

      <div class="row g-2 mb-2">
        <div class="col-md-5">
          <label class="form-label">Question text</label>
          <input
            type="text"
            class="form-control form-control-sm"
            v-model="sub.question"
            :placeholder="`Sub-question ${sIdx + 1}`"
          />
        </div>

        <div class="col-md-2">
          <label class="form-label">Type</label>
          <select
            class="form-select form-select-sm"
            v-model="sub.question_type"
            @change="onSubTypeChange(sub)"
          >
            <option value="short_answer">Short Answer</option>
            <option value="mcq">Multiple Choice</option>
            <option value="true_false">True / False</option>
          </select>
        </div>

        <div class="col-md-2">
          <label class="form-label">Marks</label>
          <input
            type="number"
            min="0"
            class="form-control form-control-sm"
            v-model.number="sub.marks"
          />
        </div>

        <div class="col-md-3">
          <label class="form-label">Difficulty</label>
          <select class="form-select form-select-sm" v-model="sub.difficulty_level">
            <option value="">Select</option>
            <option value="remembering">Remembering</option>
            <option value="understanding">Understanding</option>
            <option value="applying">Applying</option>
            <option value="analyzing">Analyzing</option>
            <option value="evaluating">Evaluating</option>
            <option value="creating">Creating</option>
          </select>
        </div>
      </div>

      <!-- MCQ -->
      <div class="mb-2" v-if="sub.question_type === 'mcq'">
        <label class="form-label">Options</label>

        <div
          v-for="(opt, oIdx) in sub.options"
          :key="oIdx"
          class="input-group input-group-sm mb-1"
        >
          <div class="input-group-text">
            <!-- radio binds to single-string accessor -->
            <input
              type="radio"
              :name="`sub_correct_${sIdx}`"
              :value="opt"
              :checked="subCorrectSingle(sub) === opt"
              @change="setSubCorrectSingle(sub, opt)"
            />
          </div>

          <input
            type="text"
            class="form-control form-control-sm"
            v-model="sub.options[oIdx]"
            :placeholder="`Option ${String.fromCharCode(65 + oIdx)}`"
          />

          <button
            v-if="sub.options.length > 2"
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="removeSubOption(sub, oIdx)"
          >
            &times;
          </button>
        </div>

        <button type="button" class="btn btn-outline-success btn-sm mt-1" @click="addSubOption(sub)">
          + Add option
        </button>
      </div>

      <!-- True/False -->
      <div class="mb-2" v-else-if="sub.question_type === 'true_false'">
        <label class="form-label d-block">Correct answer</label>

        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="radio"
            :name="`sub_tf_${sIdx}`"
            value="true"
            :checked="subCorrectSingle(sub) === 'true'"
            @change="setSubCorrectSingle(sub, 'true')"
          />
          <label class="form-check-label">True</label>
        </div>

        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="radio"
            :name="`sub_tf_${sIdx}`"
            value="false"
            :checked="subCorrectSingle(sub) === 'false'"
            @change="setSubCorrectSingle(sub, 'false')"
          />
          <label class="form-check-label">False</label>
        </div>
      </div>

      <!-- Short Answer -->
      <div class="mb-2" v-else>
        <label class="form-label">Expected answer</label>
        <input
          type="text"
          class="form-control form-control-sm"
          :value="subCorrectSingle(sub)"
          @input="setSubCorrectSingle(sub, $event.target.value)"
          placeholder="Enter expected answer"
        />
      </div>
    </div>

    <button type="button" class="btn btn-outline-primary btn-sm" @click="addSubQuestion">
      + Add sub-question
    </button>
  </div>
</template>

<script>
export default {
  name: "SubQuestionsEditor",
  props: {
    modelValue: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue"],
  computed: {
    subQuestions: {
      get() {
        return Array.isArray(this.modelValue) ? this.modelValue : [];
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
  },
  methods: {
    // --- helpers: always keep correct_answer as array in state ---
    ensureCorrectArray(sub) {
      if (!sub) return;
      if (Array.isArray(sub.correct_answer)) return;

      // legacy scalar -> wrap
      if (sub.correct_answer === null || sub.correct_answer === undefined) {
        sub.correct_answer = [];
      } else {
        sub.correct_answer = [String(sub.correct_answer)];
      }
    },

    subCorrectSingle(sub) {
      this.ensureCorrectArray(sub);
      return String(sub.correct_answer?.[0] ?? "");
    },

    setSubCorrectSingle(sub, value) {
      this.ensureCorrectArray(sub);
      const v = String(value ?? "");
      sub.correct_answer = v === "" ? [] : [v];
      this.subQuestions = this.subQuestions.slice(); // trigger update
    },

    addSubQuestion() {
      const next = this.subQuestions.slice();
      next.push({
        id: null,
        question: "",
        question_type: "short_answer",
        options: ["", ""],
        correct_answer: [""], // ✅ array
        marks: 1,
        difficulty_level: "",
      });
      this.subQuestions = next;
    },

    removeSubQuestion(idx) {
      if (this.subQuestions.length <= 1) return;
      const next = this.subQuestions.slice();
      next.splice(idx, 1);
      this.subQuestions = next;
    },

    addSubOption(sub) {
      if (!Array.isArray(sub.options)) sub.options = ["", ""];
      sub.options.push("");

      // if currently empty selection, keep it empty
      this.subQuestions = this.subQuestions.slice();
    },

    removeSubOption(sub, idx) {
      if (!Array.isArray(sub.options) || sub.options.length <= 2) return;

      const removed = sub.options.splice(idx, 1)[0];

      // if removed option was selected, clear correct
      const current = this.subCorrectSingle(sub);
      if (current === removed) {
        sub.correct_answer = [];
      }

      this.subQuestions = this.subQuestions.slice();
    },

    onSubTypeChange(sub) {
      // keep correct_answer array-form
      this.ensureCorrectArray(sub);

      if (sub.question_type === "true_false") {
        sub.options = ["true", "false"];
        const v = this.subCorrectSingle(sub);
        if (!["true", "false"].includes(v)) sub.correct_answer = [];
      } else if (sub.question_type === "mcq") {
        if (!Array.isArray(sub.options) || sub.options.length < 2) {
          sub.options = ["", ""];
        }

        // keep selection valid if possible
        const v = this.subCorrectSingle(sub);
        if (v && !sub.options.includes(v)) sub.correct_answer = [];
      } else {
        // short answer
        sub.options = [""];
        // keep existing text in correct_answer[0]
        if (sub.correct_answer.length === 0) sub.correct_answer = [""];
      }

      this.subQuestions = this.subQuestions.slice();
    },
  },
};
</script>

<style scoped>
</style>
